import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useQuery } from '@apollo/client';

import Tag from '../../../../components/V2/Tag';
import BillingAddress from '../../../../components/V2/BillingAddress';
import Money from 'core/api/Money';
import { WeekContainer } from 'onramp/ui/components';
import ModalV2 from 'core/ui/components/V2/ModalV2';
import { Text, Box, Hr, Button, Flex, Image, Link } from 'core/ui/atoms';
import { GET_ORDER_DETAILS, GET_INVOICED_USER_SWAPS } from 'core/api/apollo/queries';
import Loading from 'core/ui/components/Loading';
import { isLoading } from 'core/api/loadingHelpers';
import { PRODUCT_TEMP } from 'core/assets/images/V2';
import SplitPageSidebar from 'core/ui/components/SplitPageSidebar';
import ResponsiveView from 'core/ui/components/ResponsiveView';

const OrderDetails = ({ order, closeModal }) => {
  const { loading: orderDetailLoading, error: orderDetailError, data: { getOrderDetails = [] } = {} } = useQuery(GET_ORDER_DETAILS, {
    variables: { orderId: order?.id },
    fetchPolicy: 'network-only',
  });

  const { loading: userSwapLoading, error: invoiceError, data: { getInvoicedUserSwaps } = {} } = useQuery(GET_INVOICED_USER_SWAPS, {
    variables: { deliveryDayIds: order?.deliveryDayGqlId.toString() },
    fetchPolicy: 'network-only',
  });

  const loadingState = orderDetailLoading || userSwapLoading;
  const orderDataState = getOrderDetails || getInvoicedUserSwaps;
  const errorState = orderDetailError || invoiceError;

  if (isLoading(loadingState, orderDataState, errorState)) {
    return <Loading />;
  }

  const {
    billingTime,
    dummy,
    date,
    status,
    estimatedAmount,
    trackingUrl,
    bundleReservation,
    bundleInvoicePdfUrl,
    oneTimeProgram,
  } = getOrderDetails || {};
  const { addons, swaps, invoicePdfUrl } = getInvoicedUserSwaps[0] || {};


  const orderName = 'Signature Program';
  const bundlePrice = bundleReservation?.price;

  const calculateSubTotalAmount = () => {
    // Calculate addons total
    const addonsTotal = addons?.reduce((sum, addon) => {
      return sum + (addon?.price?.amount || 0) * (addon?.quantity || 0);
    }, 0) || 0;

    // Calculate swaps total
    const swapsTotal = swaps?.reduce((sum, swap) => {
      return sum + (swap?.price?.amount || 0) * (swap?.quantity || 1);
    }, 0) || 0;

    const total = addonsTotal + swapsTotal;

    return {
      __typename: 'Money',
      amount: total,
    };
  };

  const subtotalAmount = calculateSubTotalAmount();

  const RenderAddons = () => {
    if (!addons || addons.length === 0) {
      return null;
    }
    return addons.map((addon) =>
      <ItemContainer key={addon?.id} direction='row' justifyContent='start'>
        <SelectCont>
          <ProductImage src={PRODUCT_TEMP} />
        </SelectCont>
        <DetailsContainer>
          <ProductName>{orderName}</ProductName>
          <SubTitle>Quantity: ${addon?.quantity}</SubTitle>
        </DetailsContainer>
      </ItemContainer>
    );
  };

  const RenderSwaps = () => {
    if (!swaps || swaps.length === 0) {
      return null;
    }
    return swaps.map((swap) =>
      <ItemContainer key={swap?.id} direction='row' justifyContent='start'>
        <SelectCont>
          <ProductImage src={PRODUCT_TEMP} />
        </SelectCont>
        <DetailsContainer>
          <ProductName>{swap?.replacedComponentName}</ProductName>
        </DetailsContainer>
      </ItemContainer>
    );
  };

  const RenderSideBar = () => (
    <>
      <TitleCenter>Information</TitleCenter>
      <BillingAddress title='Shipping Address' edit={false} />
    </>
  );

  return (
    <ModalV2 fullWidth={true} width='90%' data-account-info-modal onClose={closeModal}>
      <ModalTitle>Order Details</ModalTitle>
      <Hr />
      <SplitPageSidebar
        isModal={true}
        sidebarWidth='50%'
        sidebarView={<RenderSideBar />}
      >
        <WeekContainer>{orderName}</WeekContainer>
        <Container>
          <Flex direction='row' justifyContent='space-between' alignItems='center'>
            <BillingCycleText>
              Billing cycle date: {moment(billingTime).format('MMM DD, YYYY')}
            </BillingCycleText>
            {status && <Tag text={status} style={dummy ? 'outline' : 'default'} />}
          </Flex>
          <ItemContainer direction='row' justifyContent='start'>
            <SelectCont>
              <ProductImage src={PRODUCT_TEMP} alt={name} />
            </SelectCont>
            <DetailsContainer>
              <ProductName>{orderName}</ProductName>
              <SubTitle>Breakfast + Lunch + Dinner</SubTitle>
              {oneTimeProgram?.subscriptionActive &&
                <SubTitle>
                  Subscription | {oneTimeProgram?.programBundle?.schedule?.name}/ Week
                </SubTitle>
              }
              <SubTitle>Delivery Week: {moment(date).format('MMM DD, YYYY')}</SubTitle>
            </DetailsContainer>
          </ItemContainer>
          <Flex direction='row' justifyContent='space-between' alignItems='center'>
            <SubscriptionType>Weekly Subscription</SubscriptionType>
            <SubscriptionType>{Money(bundlePrice).toFormat('$0,0')}</SubscriptionType>
          </Flex>
          {bundleInvoicePdfUrl &&
            <Flex direction='row' justifyContent='center'>
              <ButtonUnderLine href={bundleInvoicePdfUrl} target='_blank'>
                DOWNLOAD INVOICE
              </ButtonUnderLine>
            </Flex>
          }
          <Hr />
          <SectionTitle>Meal Swaps & Add-Ons</SectionTitle>
          <RenderSwaps />
          <RenderAddons />
          <Flex direction='row' justifyContent='space-between' alignItems='center'>
            <SubscriptionType>Sub Total:</SubscriptionType>
            <SubscriptionType>{Money(subtotalAmount).toFormat('$0,0')}</SubscriptionType>
          </Flex>
          {invoicePdfUrl &&
            <Flex direction='row' justifyContent='center'>
              <ButtonUnderLine href={invoicePdfUrl} target='_blank'>
                DOWNLOAD INVOICE
              </ButtonUnderLine>
            </Flex>
          }
          <TotalButton>Total: {Money(estimatedAmount).toFormat('$0,0')}</TotalButton>
          <ResponsiveView desktop={false}>
            <Hr />
            <TitleCenter>Information</TitleCenter>
            <BillingAddress title='Shipping Address' edit={false} />
            {trackingUrl &&
              <Flex direction='row' justifyContent='center' alignItems='center'>
                <TrackButton href={trackingUrl} target='_blank'>Track Order</TrackButton>
              </Flex>
            }
          </ResponsiveView>
        </Container>
      </SplitPageSidebar>
      {trackingUrl &&
        <ResponsiveView desktop={true}>
          <Flex direction='row' justifyContent='center' alignItems='center'>
            <DesktopTrackButton href={trackingUrl} target='_blank'>Track Order</DesktopTrackButton>
          </Flex>
        </ResponsiveView>
      }
    </ModalV2>
  );
};

OrderDetails.propTypes = {
  order: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  orderDetail: PropTypes.object.isRequired,
  invoicedSwap: PropTypes.object.isRequired,
};

const TitleCenter = styled(Text)`
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  text-align: center;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
`;

const TrackButton = styled(Link)`
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  border-radius: 4px;
  background: #C5D1C0;
  display: flex;
  width: 90%;
  padding: 12px 0;
  justify-content: center;
  align-items: center;
  gap: 10px;
  text-align: center;
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

const DesktopTrackButton = styled(TrackButton)`
  width: 35%;
`;

const TotalButton = styled(Button)`
  width: 100%;
  height: 38px;
  border-radius: 4px;
  display: flex;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgba(197, 209, 192, 0.24);
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
`;

const SectionTitle = styled(Text)`
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const SubscriptionType = styled(Text)`
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 16px;
  font-style: normal;
  font-weight: 800;
  line-height: 135%;
`;

const BillingCycleText = styled(Text)`
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
`;

const Container = styled(Box)`
  padding-left: 25px;
  padding-right: 25px;
  margin-bottom: 30px;
`;

const ButtonUnderLine = styled(Link)`
  text-decoration: underline;
  text-underline-offset: 5px;
  margin-bottom: 25px;
  margin-top: 10px;
  color: ${(p) => p.theme.colors.primaryV2};
  text-align: center;
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 1.2px;
  text-transform: uppercase;
`;

const ItemContainer = styled(Flex)`
  width: 100%;
`;

const SubTitle = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-size: 12px;
  line-height: 22px;
  margin: 0px;
  color: ${(p) => p.theme.colors.primaryV2};
`;

const ProductImage = styled(Image)`
  height: 94px;
  width: 94px;
  object-fit: cover;
`;

const SelectCont = styled.div`
  position: relative;
  width: 28%;
  display: flex;
  justify-content: end;
  flex-shrink: 0;
  margin-right: 10px;
  &:last-child {
    margin-right: 0;
  }
`;

const DetailsContainer = styled(Box)`
  width: 60%;
`;

const ProductName = styled.p`
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 16px;
  line-height: 22px;
  margin: 0px;
  color: ${(p) => p.theme.colors.primaryV2};
`;

const ModalTitle = styled(Text)`
  margin-top: 70px;
  text-align: center;
  font-size: 30px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0.9px;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  color: ${({ theme }) => theme.colors.darkGreen};
`;

export default OrderDetails;
